import React, { FC, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useToast } from 'app/hooks/useToast';
import { useMutation } from 'react-query';
import PostApi, { ScheduleResponse } from 'api/post';
import { PostStatus, STATUS } from '@kontentino/kontentino-constants/Posts';
import { TYPE as PAGE_TYPE } from '@kontentino/kontentino-constants/Pages';
import Logger from 'utils/logger';
import PopupLoading from 'components/posts/detail/overview/footer/popups/PopupLoading';
import Popup from 'utils/popup';
import { ApiClientError } from 'api/client';
import UserpilotUtils from 'app/utils/userpilot';
import { useEffectOnce } from 'utils/hooks/useEffectOnce';
import { Alert } from '@kontentino/ui';
import { copyTextToClipboard } from '@kontentino/kontentino-utils/clipboard';
import { useTranslation } from 'react-i18next';
import { PostPopups } from 'app/modules/posts/modules/post/hooks/usePostPopups';
import { useScheduleActionAnimationContext } from 'app/modules/posts/modules/post/components/ScheduleActionAnimationProvider';
import DataLayer from 'app/utils/dataLayer';
import { PostDetail } from 'types/PostDetail';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import DateUtils from 'app/utils/date';

dayjs.extend(utc);
dayjs.extend(timezone);

const PUBLISH_NOW_VIA_SCHEDULER_PAGE_TYPES: number[] = [PAGE_TYPE.PINTEREST];

type Props = {
  post: PostDetail;
  hidePopup: () => void;
  onUpdated: (status: PostStatus) => void;
  type: PostPopups;
};

const SchedulePopup: FC<Props> = (props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const direct = useMutation(PostApi.schedule, {
    onError,
    onSuccess: (data) => {
      onSuccess(data);
      UserpilotUtils.track(UserpilotUtils.events.POST_SCHEDULED);
    },
  });
  const darkPost = useMutation(PostApi.scheduleDarkPost, {
    onError,
    onSuccess,
  });
  const publishNow = useMutation(PostApi.publishNow, {
    onError,
    onSuccess,
  });
  const { showAnimation } = useScheduleActionAnimationContext();
  // A published post is asked to confirm before anything runs; start busy so the popup
  // never paints an empty frame while the dialog is open.
  const [isConfirming, setIsConfirming] = useState(
    props.post.was_published && props.type !== PostPopups.ScheduleDarkPost,
  );

  useEffectOnce(() => {
    void schedule();
  });

  function onSuccess(data: ScheduleResponse | { id: string }) {
    DataLayer.push({
      event: DataLayer.events.SCHEDULE_POST,
      type: props.type,
      data: props.post,
      response: data,
    });
  }

  function onError(e: ApiClientError) {
    DataLayer.push({
      event: DataLayer.events.SCHEDULE_POST_ERROR,
      type: props.type,
      data: props.post,
      error: e.userMessage ?? 'Schedule post error',
    });
    toast(e.userMessage ?? t('unableToSchedulePost'), 'error');
    props.hidePopup();
  }

  async function schedule() {
    if (props.type === PostPopups.ScheduleDarkPost) {
      scheduleDarkPost();
      return;
    }

    const confirmRepublish = props.post.was_published;

    if (confirmRepublish) {
      setIsConfirming(true);
      const isConfirmed = await askToPublishAgain();
      setIsConfirming(false);

      if (!isConfirmed) {
        props.hidePopup();
        return;
      }
    }

    switch (props.type) {
      case PostPopups.Schedule:
        scheduleDirect({ chosenMethod: 'publish', confirmRepublish });
        break;
      case PostPopups.ScheduleExportInAdvance:
        scheduleDirect({ chosenMethod: 'export', confirmRepublish });
        break;
      case PostPopups.SchedulePublishNow:
        if (
          PUBLISH_NOW_VIA_SCHEDULER_PAGE_TYPES.includes(props.post.page.type)
        ) {
          scheduleDirect({
            chosenMethod: 'export',
            isPublishNow: true,
            confirmRepublish,
          });
        } else {
          schedulePublishNow(confirmRepublish);
        }
        break;
    }
  }

  async function askToPublishAgain(): Promise<boolean> {
    const { isConfirmed } = await Popup.confirm({
      title: t('postAlreadyPublished', { pageName: props.post.page.name }),
      text: t('postAlreadyPublishedWarning', {
        pageName: props.post.page.name,
      }),
      confirmButtonText: t('publishAgain'),
      customClass: {
        popup: '!tw-w-[400px]',
      },
    });

    return isConfirmed;
  }

  async function scheduleDirect({
    chosenMethod,
    isPublishNow = false,
    confirmRepublish = false,
  }: {
    chosenMethod: 'publish' | 'export';
    isPublishNow?: boolean;
    confirmRepublish?: boolean;
  }) {
    try {
      const res = await direct.mutateAsync({
        ids: [props.post.id],
        chosenMethod,
        confirmRepublish,
      });

      const postResult = res[props.post.id];

      if (postResult) {
        const { status: postExported } = postResult;

        if (postExported) {
          showAnimation(props.post.id);

          let resultStatus: PostStatus;
          if (isPublishNow) {
            resultStatus = STATUS.POSTED;
          } else if (chosenMethod === 'export') {
            resultStatus = STATUS.EXPORTED;
          } else {
            resultStatus = STATUS.SCHEDULED;
          }

          props.onUpdated(resultStatus);
        } else {
          toast(
            postResult.message ||
              (typeof postResult.data === 'string'
                ? postResult.data
                : undefined),
            'error',
          );
        }
      }
    } catch (e) {
      Logger.error(e);
    }
    props.hidePopup();
  }

  async function scheduleDarkPost() {
    try {
      const res = await darkPost.mutateAsync(props.post.id);

      if (typeof res?.id === 'string') {
        const link = `https://www.facebook.com/${res.id}`;
        Popup.common({
          title: t('darkPostCreated'),
          showDenyButton: true,
          denyButtonText: t('copyLink'),
          customClass: {
            denyButton: 'swal2-cancel cy-dark-post-deny-button',
            confirmButton: 'cy-dark-post-confirm-button',
            popup: 'cy-dark-post-created-modal',
            actions: 'cy-dark-post-actions',
          },

          html: ReactDOMServer.renderToString(
            <Alert>
              <span data-cy="dark-post-facebook-link">{link}</span>
            </Alert>,
          ),
          async preDeny() {
            try {
              await copyTextToClipboard(link);
              toast(t('copiedToClipboard'));
            } catch (e) {
              toast(t('somethingWentWrong'), 'error');
            }
            return false;
          },
        });
        //   no status is updated dark post is created only on fb side
      } else {
        toast(t('failedToCreateDarkPost'), 'error');
      }
    } catch (e) {
      Logger.error(e);
    }
    props.hidePopup();
  }

  async function schedulePublishNow(confirmRepublish = false) {
    try {
      if (props.post.page.timezone) {
        const nowInPageTimezone = dayjs().tz(props.post.page.timezone);
        const newPostDate = DateUtils.toDateString(nowInPageTimezone);
        const newPostTime = DateUtils.toTimeString(nowInPageTimezone);

        const res = await publishNow.mutateAsync({
          id: props.post.id,
          date: newPostDate,
          time: newPostTime,
          confirmRepublish,
        });

        if (typeof res?.id === 'string') {
          props.onUpdated(STATUS.POSTED);
        } else {
          toast(t('failedToPublishPostNow'), 'error');
        }
      }
    } catch (e) {
      Logger.error(e);
    }
    props.hidePopup();
  }

  const isLoading =
    isConfirming ||
    direct.isLoading ||
    darkPost.isLoading ||
    publishNow.isLoading;

  return (
    <div>
      {isLoading && (
        <PopupLoading>
          {props.type === PostPopups.ScheduleDarkPost
            ? t('schedulingDarkPost')
            : t('schedulingPost')}
        </PopupLoading>
      )}
    </div>
  );
};

export default SchedulePopup;
